import { useState, useMemo, useCallback } from 'react';
import type { Pageable } from '../../core/api/spring-types';
import { useSpringReport } from './use-spring-report';
import { useReportDataAdapters } from './use-report-data-adapters';
import { useReportExport } from './use-report-export';
import { useEditions } from './use-editions';
import { ALL_EDITIONS_SENTINEL } from '../constants/editions';

/**
 * Configuration for useReportPage hook
 *
 * @template TDto - The DTO type for the report data
 */
export interface UseReportPageConfig<TDto> {
    /**
     * React Query Kit query hook (e.g., api.schools.report)
     * Must have a `.useQuery()` method that accepts { variables: { filter, pageable } }
     */
    queryHook: {
        useQuery: (params: { variables: { filter: Partial<TDto>; pageable: Pageable } }) => any;
    };

    /**
     * Initial sort order (e.g., ['numeInstitutieInvatamant,asc'])
     * @default []
     */
    initialSort?: string[];

    /**
     * Initial page size
     * @default 20
     */
    initialPageSize?: number;

    /**
     * Export endpoint for Excel download (e.g., '/api/schools/report')
     */
    exportEndpoint: string;

    /**
     * Export filename without extension (e.g., 'raport_scoli')
     */
    exportFilename: string;

    /**
     * Initial filter values (optional)
     * @default {}
     */
    initialFilter?: Partial<TDto>;

    /**
     * Extra export params to append to URL (e.g., { editionId: '123' })
     * @default {}
     */
    extraExportParams?: Record<string, string>;

    /**
     * Enable edition support (adds edition selector, auto-filters by edition)
     * When enabled, automatically manages edition state and filters data by selected edition
     * @default false
     */
    withEditions?: boolean;

    /**
     * Field name in filter object for edition ID
     * Used when withEditions=true to inject edition filter
     * @default 'editionId'
     */
    editionFilterKey?: string;

    /**
     * Permite selectarea "toate edițiile" (ADMIN). @default false — zero schimbare pt. paginile
     * existente cu withEditions=true (ex. SchoolRequestsReportPage).
     */
    allowAllEditions?: boolean;

    /**
     * Cheia din filtru trimisă la backend cand isAllEditions e true. @default 'toateEditiile'
     */
    allEditionsFilterKey?: string;

    /**
     * Traduce filtrul grilei in cheile endpointului de export (cand exportul foloseste alt DTO
     * decat grila). Se paseaza mai departe lui `useReportExport`.
     */
    mapExportFilter?: (filter: Record<string, any>) => Record<string, any>;
}

/**
 * Consolidated hook for report pages - eliminates 50+ lines of boilerplate
 *
 * Combines:
 * - useState for filter and pageable
 * - Query execution via React Query Kit
 * - useSpringReport for report interface
 * - useReportDataAdapters for JSX compatibility
 * - useReportExport for Excel export
 * - Optional edition support with auto-filtering
 *
 * @example Basic usage
 * ```tsx
 * function SchoolsReportPage() {
 *     const { report, adapters, handleExport } = useReportPage({
 *         queryHook: api.schools.report,
 *         initialSort: ['numeInstitutieInvatamant,asc'],
 *         exportEndpoint: '/api/schools/report',
 *         exportFilename: 'raport_scoli'
 *     });
 *
 *     const columns = useMemo(() => columnBuilder()...);
 *
 *     return (
 *         <ReportPageLayout
 *             report={report}
 *             adapters={adapters}
 *             handleExport={handleExport}
 *             columns={columns}
 *         />
 *     );
 * }
 * ```
 *
 * @example With edition support
 * ```tsx
 * function EditionReportPage() {
 *     const { report, adapters, handleExport, editions, currentEdition, changeEdition } = useReportPage({
 *         queryHook: api.schools.statusReport,
 *         exportEndpoint: '/api/schools/status-report',
 *         exportFilename: 'raport_editie',
 *         withEditions: true  // Auto-filters by selected edition
 *     });
 *
 *     const columns = useMemo(() => columnBuilder()..., []);
 *
 *     return (
 *         <ReportPageLayout
 *             report={report}
 *             adapters={adapters}
 *             handleExport={handleExport}
 *             columns={columns}
 *             extraControls={
 *                 <EditionSelector
 *                     editions={editions}
 *                     currentEditionId={currentEdition?.id}
 *                     onChange={changeEdition}
 *                 />
 *             }
 *         />
 *     );
 * }
 * ```
 *
 * @template TDto - The DTO type for the report data
 * @param config - Configuration object
 * @returns Report interface, adapters, export handler, and optional edition data
 */
export function useReportPage<TDto extends Record<string, any>>({
    queryHook,
    initialSort = [],
    initialPageSize = 20,
    exportEndpoint,
    exportFilename,
    initialFilter = {},
    extraExportParams = {},
    withEditions = false,
    editionFilterKey = 'editionId',
    allowAllEditions = false,
    allEditionsFilterKey = 'toateEditiile',
    mapExportFilter
}: UseReportPageConfig<TDto>) {
    // ==========================================
    // 1. STATE MANAGEMENT
    // ==========================================

    const [filter, setFilter] = useState<Partial<TDto>>(initialFilter);
    const [pageable, setPageable] = useState<Pageable>({
        page: 0,
        size: initialPageSize,
        sort: initialSort
    });

    // ==========================================
    // 2. EDITION SUPPORT (conditional)
    // ==========================================

    // Conditionally call useEditions hook based on withEditions flag
    // React pattern: Call hook unconditionally, but return null when disabled
    const editionsData = useEditions();

    // Stare separata de `useEditions()` — NU un sentinel pe `selectedEditionId`, care ar coincide
    // cu starea "inca se incarca" si ar sparge invariantul neincheiata/editiiEsuate/succes de mai jos.
    const [isAllEditions, setIsAllEditions] = useState(false);

    const changeEditionOrAll = useCallback((value: string) => {
        if (allowAllEditions && value === ALL_EDITIONS_SENTINEL) {
            setIsAllEditions(true);
            return;
        }
        setIsAllEditions(false);
        editionsData?.changeEdition?.(value);
    }, [allowAllEditions, editionsData?.changeEdition]);

    // Auto-merge edition filter into query filter when editions are enabled
    const queryFilter = useMemo(() => {
        if (withEditions && isAllEditions) {
            return {
                ...filter,
                [allEditionsFilterKey]: true
            } as Partial<TDto>;
        }
        if (withEditions && editionsData?.currentEdition) {
            return {
                ...filter,
                [editionFilterKey]: editionsData.currentEdition.id
            } as Partial<TDto>;
        }
        return filter;
    }, [filter, withEditions, isAllEditions, editionsData?.currentEdition, editionFilterKey, allEditionsFilterKey]);

    // ==========================================
    // 3. DATA FETCHING
    // ==========================================

    const query = queryHook.useQuery({
        variables: { filter: queryFilter, pageable },
        // When editions are enabled, wait for edition to load before executing query
        // This prevents duplicate requests: one without editionId, one with editionId
        enabled: withEditions ? (isAllEditions || !!editionsData?.currentEdition) : true
    });

    // ==========================================
    // 4. REPORT INTERFACE
    // ==========================================

    const reportBrut = useSpringReport<TDto, Partial<TDto>>({
        queryResult: query,
        filterState: [filter, setFilter],
        pageableState: [pageable, setPageable],
    });

    /* 🔴 DOUA surse de esec, nu una — si a doua nu se vede in `query`.
       Cand `withEditions` e activ, interogarea principala porneste doar cu
       `enabled: !!currentEdition` (mai jos). Daca interogarea de EDITII pica,
       `currentEdition` ramane `null`, principala nu ruleaza niciodata, si atunci
       `query.isError` e `false` — ecranul afiseaza „TOTAL … 0" fara nicio eroare.
       Masurat pe transport mort: 3 din 4 rapoarte cad pe calea asta, iar o
       reparatie care s-ar uita doar la `query.isError` ar repara exact unul.

       `retry` reface AMBELE. Nu e nevoie de un al doilea gest dupa ce editiile
       revin: `currentEdition` se seteaza, `enabled` devine `true`, si React Query
       porneste singur interogarea principala. */
    const editiiEsuate = withEditions && editionsData?.isError === true;

    /* 🔴 INVARIANTUL, formulat de S1 dupa o masuratoare pe PROD: regiunea de
       totaluri si tabelul NU randeaza niciodata un numar care nu e sustinut de o
       interogare INCHEIATA CU SUCCES.

       Cu editiile doar LENTE (200 OK in 6 s), `/rapoarte/comenzi` la 360 arata
       „TOTAL COMENZI 0" + „Nu există date." timp de secunde, fara niciun indiciu
       de incarcare — si `isError` e fals pe toata durata. Deci eroarea era doar
       un caz particular; starea care minte e „interogarea principala n-a rulat
       inca", din care esecul e o iesire, iar lentoarea alta.

       Trei stari, nu doua:
         NEINCHEIATA          → schelet, fara cifre si fara „Nu există date."
         ESUATA (orice sursa) → starea de eroare, cu „Reîncearcă"
         INCHEIATA CU SUCCES  → date, inclusiv un zero REAL

       `asteaptaEditia` e scris explicit, desi azi e acoperit si de
       `query.isPending` (o interogare dezactivata e `pending` in React Query v5):
       daca semantica lui `enabled` se schimba, invariantul nu trebuie sa depinda
       de un detaliu al bibliotecii. */
    const editiiNeincheiate = withEditions && editionsData?.isLoading === true;
    const asteaptaEditia = withEditions && !editionsData?.currentEdition && !editiiEsuate;
    const neincheiata = editiiNeincheiate || asteaptaEditia || query.isPending === true;

    const report = useMemo(
        () => ({
            ...reportBrut,
            isError: reportBrut.isError || editiiEsuate,
            /* Esecul BATE nefinalizarea: o interogare esuata e tot „pending" cat
               timp precerinta ei lipseste, si atunci s-ar roti un schelet la
               nesfarsit, fara „Reîncearcă". */
            isPending: neincheiata && !(reportBrut.isError || editiiEsuate),
            retry: () => {
                if (editiiEsuate) editionsData?.retry?.();
                reportBrut.refetch();
            }
        }),
        [reportBrut, editiiEsuate, neincheiata, editionsData]
    );

    // ==========================================
    // 5. ADAPTERS (JSX compatibility layer)
    // ==========================================

    const adapters = useReportDataAdapters(report);

    // ==========================================
    // 6. EXPORT FUNCTIONALITY
    // ==========================================

    // Auto-include edition in export params when editions are enabled
    const exportParams = useMemo(() => {
        if (withEditions && isAllEditions) {
            return {
                ...extraExportParams,
                [allEditionsFilterKey]: 'true'
            };
        }
        if (withEditions && editionsData?.currentEdition) {
            return {
                ...extraExportParams,
                [editionFilterKey]: editionsData.currentEdition.id
            };
        }
        return extraExportParams;
    }, [extraExportParams, withEditions, isAllEditions, editionsData?.currentEdition, editionFilterKey, allEditionsFilterKey]);

    const handleExport = useReportExport({
        endpoint: exportEndpoint,
        filter: report.filter,
        filename: exportFilename,
        extraParams: exportParams,
        mapFilter: mapExportFilter
    });

    // ==========================================
    // 7. RETURN INTERFACE
    // ==========================================

    return {
        /**
         * Main report interface with data, loading state, pagination controls, etc.
         */
        report,

        /**
         * Adapters for JSX components (legacy compatibility)
         * TODO: Remove once all components migrate to TypeScript
         */
        adapters,

        /**
         * Excel export handler
         */
        handleExport,

        /**
         * Direct filter access (for edge cases)
         */
        filter,

        /**
         * Direct filter setter (for custom filter logic)
         */
        setFilter,

        /**
         * Direct pageable access (for edge cases)
         */
        pageable,

        /**
         * Direct pageable setter (for custom pagination logic)
         */
        setPageable,

        // Edition-specific data (null when withEditions=false)

        /**
         * Available editions (null if withEditions=false)
         */
        editions: withEditions ? editionsData?.editions || [] : null,

        /**
         * Currently selected edition (null if withEditions=false)
         */
        currentEdition: withEditions ? editionsData?.currentEdition || null : null,

        /**
         * Edition change handler (null if withEditions=false). Cand `allowAllEditions` e activ si
         * i se paseaza sentinel-ul `ALL_EDITIONS_SENTINEL`, comuta pe `isAllEditions` in loc sa
         * treaca valoarea mai departe la `useEditions()`.
         */
        changeEdition: withEditions ? changeEditionOrAll : null,

        /**
         * True cand utilizatorul a ales "Toate edițiile" (necesită allowAllEditions=true).
         * Mereu false cand withEditions=false sau allowAllEditions=false.
         */
        isAllEditions: withEditions && allowAllEditions ? isAllEditions : false,
    };
}
